// import { useState, useEffect, useMemo } from "react";

// /* Track mouse relative to image + viewport */
// function useMouse(ref) {
//   const [mouse, setMouse] = useState({
//     x: 0,
//     y: 0,
//     clientX: 0,
//     clientY: 0,
//     isActive: false,
//   });

//   useEffect(() => {
//     if (!ref.current) return;

//     const handleMouseMove = (e) => {
//       const rect = ref.current.getBoundingClientRect();
//       setMouse({
//         x: e.clientX - rect.left,
//         y: e.clientY - rect.top,
//         clientX: e.clientX,
//         clientY: e.clientY,
//         isActive: true,
//       });
//     };

//     const handleMouseOut = () => {
//       setMouse((m) => ({ ...m, isActive: false }));
//     };

//     const el = ref.current;
//     el.addEventListener("mousemove", handleMouseMove);
//     el.addEventListener("mouseout", handleMouseOut);

//     return () => {
//       el.removeEventListener("mousemove", handleMouseMove);
//       el.removeEventListener("mouseout", handleMouseOut);
//     };
//   }, [ref]);

//   return mouse;
// }

// export function useMouseOverZoom(
//   source,
//   target,
//   cursor,
//   radius = 50
// ) {
//   const mouse = useMouse(source);
//   const { x, y, isActive } = mouse;

//   const zoomBounds = useMemo(
//     () => ({
//       left: x - radius,
//       top: y - radius,
//       width: radius * 2,
//       height: radius * 2,
//     }),
//     [x, y, radius]
//   );

//   const isImageValid =
//     source.current?.complete && source.current?.naturalWidth > 0;

//   /* Cursor overlay */
//   useEffect(() => {
//     if (!cursor.current || !isImageValid) return;

//     const { left, top, width, height } = zoomBounds;
//     cursor.current.style.left = `${left}px`;
//     cursor.current.style.top = `${top}px`;
//     cursor.current.style.width = `${width}px`;
//     cursor.current.style.height = `${height}px`;
//     cursor.current.style.display = isActive ? "block" : "none";
//   }, [zoomBounds, isActive, cursor, isImageValid]);

//   /* Draw zoomed area */
//   useEffect(() => {
//     if (!target.current || !source.current || !isImageValid) return;

//     const ctx = target.current.getContext("2d");
//     if (!ctx) return;

//     if (!isActive) {
//       ctx.clearRect(0, 0, target.current.width, target.current.height);
//       return;
//     }

//     const { left, top, width, height } = zoomBounds;
//     const ratio = source.current.naturalWidth / source.current.width;

//     ctx.clearRect(0, 0, target.current.width, target.current.height);
//     ctx.drawImage(
//       source.current,
//       left * ratio,
//       top * ratio,
//       width * ratio,
//       height * ratio,
//       0,
//       0,
//       target.current.width,
//       target.current.height
//     );
//   }, [zoomBounds, isActive, source, target, isImageValid]);

//   return mouse;
// }

// ========================

import { useState, useEffect, useMemo } from 'react';

function useMouse(ref) {
  const [mouse, setMouse] = useState({
    x: 0,
    y: 0,
    clientX: 0,
    clientY: 0,
    isActive: false,
  });

  useEffect(() => {
    const img = ref.current;
    if (!img) return;

    const handleMove = (e) => {
      const rect = img.getBoundingClientRect();

      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      setMouse({
        x,
        y,
        clientX: e.clientX,
        clientY: e.clientY,
        isActive: x >= 0 && x <= rect.width && y >= 0 && y <= rect.height,
      });
    };

    const handleLeave = () => setMouse((m) => ({ ...m, isActive: false }));

    img.addEventListener('mousemove', handleMove);
    img.addEventListener('mouseleave', handleLeave);

    return () => {
      img.removeEventListener('mousemove', handleMove);
      img.removeEventListener('mouseleave', handleLeave);
    };
  }, [ref]);

  return mouse;
}

export function useMouseOverZoom(source, target, cursor, radius = 50) {
  const mouse = useMouse(source);
  const { x, y, isActive } = mouse;

  const zoomBounds = useMemo(
    () => ({
      left: x - radius,
      top: y - radius,
      width: radius * 2,
      height: radius * 2,
    }),
    [x, y, radius],
  );

  const isImageValid =
    !!source.current && source.current.complete && source.current.naturalWidth > 0;

  /* Cursor */
  useEffect(() => {
    if (!cursor.current || !source.current) return;

    if (!isActive) {
      cursor.current.style.display = 'none';
      return;
    }

    const rect = source.current.getBoundingClientRect();

    let left = x - radius;
    let top = y - radius;

    left = Math.max(0, Math.min(left, rect.width - radius * 2));
    top = Math.max(0, Math.min(top, rect.height - radius * 2));

    cursor.current.style.display = 'block';
    cursor.current.style.left = `${left}px`;
    cursor.current.style.top = `${top}px`;
    cursor.current.style.width = `${radius * 2}px`;
    cursor.current.style.height = `${radius * 2}px`;
  }, [x, y, radius, isActive]);
  /* Zoom */
  useEffect(() => {
    if (!target.current || !source.current) return;

    const canvas = target.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!isActive) return;

    const img = source.current;

    const rect = img.getBoundingClientRect();

    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;

    let sx = (x - radius) * scaleX;
    let sy = (y - radius) * scaleY;

    const sw = radius * 2 * scaleX;
    const sh = radius * 2 * scaleY;

    sx = Math.max(0, Math.min(sx, img.naturalWidth - sw));
    sy = Math.max(0, Math.min(sy, img.naturalHeight - sh));

    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
  }, [x, y, radius, isActive]);
  return mouse;
}
